import {
	IconBan,
	IconBell,
	IconBrandZapier,
	IconChartLine,
	IconCreditCard,
	IconFileText,
	IconMail,
	IconPalette,
	IconUser,
	IconUsers,
} from "@tabler/icons-react"
import type React from "react"
import styled from "styled-components"

import { LinkIcon } from "~/components/Icon"
import Layout, { PageTitle } from "~/components/Layout"
import Link from "~/components/Link"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { componentBackgroundColor, componentBorderColor, lightTextColor, textColor } from "~/utilities/styles"

const SettingsPage: React.FC = () => {
	const platform = usePlatform()!

	return (
		<Layout>
			<PageTitle>Settings</PageTitle>
			<SettingsItems>
				<SettingsItem to={routes.settings.team()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconUsers} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Team Members</SettingsItemTitle>
						<SettingsItemSubtitle>
							Manage your academy team members or add more people to your team
						</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.customizeAcademy()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconPalette} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Customize Academy</SettingsItemTitle>
						<SettingsItemSubtitle>Change the appearance and make it yours</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.studentEmail.index()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconMail} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Email Automation</SettingsItemTitle>
						<SettingsItemSubtitle>Customize the emails your students receive</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.domain.index()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={LinkIcon} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Custom Domain</SettingsItemTitle>
						<SettingsItemSubtitle>Set a custom domain for your academy</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.legal()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconFileText} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Legal</SettingsItemTitle>
						<SettingsItemSubtitle>Replace the public terms and policy documents</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.notification()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconBell} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Notifications</SettingsItemTitle>
						<SettingsItemSubtitle>
							Configure which notifications you want to receive by email.
						</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.tracking()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconChartLine} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Tracking</SettingsItemTitle>
						<SettingsItemSubtitle>Meta Pixel for signup and purchase tracking</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				{platform.plan.access.zapier && (
					<SettingsItem to={routes.settings.zapier()}>
						<SettingsItemIconContainer>
							<SettingsItemIcon as={IconBrandZapier} />
						</SettingsItemIconContainer>
						<SettingsItemDetails>
							<SettingsItemTitle>Zapier</SettingsItemTitle>
							<SettingsItemSubtitle>Connect your academy with Zapier</SettingsItemSubtitle>
						</SettingsItemDetails>
					</SettingsItem>
				)}

				<SettingsItem to={routes.settings.bannedIp()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconBan} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Banned IPs</SettingsItemTitle>
						<SettingsItemSubtitle>Ban IP addresses from accessing your academy</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.billing.plans()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconCreditCard} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Billing &amp; Plans</SettingsItemTitle>
						<SettingsItemSubtitle>Upgrade your plan or change your billing settings</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>

				<SettingsItem to={routes.settings.account()}>
					<SettingsItemIconContainer>
						<SettingsItemIcon as={IconUser} />
					</SettingsItemIconContainer>
					<SettingsItemDetails>
						<SettingsItemTitle>Account</SettingsItemTitle>
						<SettingsItemSubtitle>Change your name or password</SettingsItemSubtitle>
					</SettingsItemDetails>
				</SettingsItem>
			</SettingsItems>
		</Layout>
	)
}

const SettingsItems = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
	gap: 16px;
`

const SettingsItem = styled(Link)`
	background-color: ${componentBackgroundColor};
	border: 1px solid ${componentBorderColor};
	border-radius: 24px;
	padding: 24px;
	display: flex;
	align-items: flex-start;
	gap: 16px;
	transition: background-color 150ms, border-color 150ms;

	&:hover {
		background-color: ${props => props.theme.primaryColor}08;
		border-color: ${props => props.theme.primaryColor}30;
	}

	&:focus-visible {
		outline: 2px solid ${props => props.theme.primaryColor};
		outline-offset: 2px;
	}
`

const SettingsItemIconContainer = styled.div`
	width: 44px;
	height: 44px;
	flex-shrink: 0;
	color: ${props => props.theme.primaryColor};
	background: ${props => props.theme.primaryColor}14;
	padding: 10px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
`

const SettingsItemIcon = styled.div`
	width: 100%;
	height: 100%;
`

const SettingsItemDetails = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`

const SettingsItemTitle = styled.h2`
	color: ${textColor};
	font-weight: 600;
	font-size: 18px;
	text-align: left;
`

const SettingsItemSubtitle = styled.p`
	color: ${lightTextColor};
	font-size: 15px;
	text-align: left;
`

export default SettingsPage
